import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

import ModalSheet from "./ModalSheet";

import { ModalSheetControllerProps } from "./ModalSheet.types";

const DRAG_CLOSE_DISTANCE = 100;

const ModalSheetContainer = ({
  className,
  portalClassName,
  open = false,
  centered = true,
  children,
  closeTimeoutMS = 300,
  title,
  position,
  onClose = () => { },
}: ModalSheetControllerProps) => {
  let pathname: string | null = null;
  let hasLocation = false;

  try {
    const loc = useLocation();
    pathname = loc.pathname;
    hasLocation = true;
  } catch (e) {
    hasLocation = false;
  }

  const startY = useRef<number | null>(null);
  const [translateY, setTranslateY] = useState("100%");
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    return () => {
      document.body.style.removeProperty('--scrollbar-compensate');
      document.body.classList.remove('ReactModal__Body--open');
    };
  }, [hasLocation ? pathname : null]);

  const modalOpenCallback = () => {
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.setProperty('--scrollbar-compensate', `${scrollbarWidth}px`);
    setTimeout(() => {
      document.body.classList.add('ReactModal__Body--open');
    });
  }

  const modalCloseCallback = () => {
    document.body.style.removeProperty('--scrollbar-compensate');
    document.body.classList.remove('ReactModal__Body--open');
  }


  useEffect(() => {
    if (open) {
      setTranslateY("0");
    } else {
      setTranslateY("100%");
    }
  }, [open]);

  const handleDragStart = (event: React.MouseEvent | React.TouchEvent) => {
    if ("touches" in event) {
      startY.current = (event as React.TouchEvent).touches[0].clientY;
    } else {
      startY.current = (event as React.MouseEvent).clientY;
    }
    setIsDragging(true);
    window.addEventListener("mousemove", handleDragMove as any);
    window.addEventListener("touchmove", handleDragMove as any, { passive: false });
    window.addEventListener("mouseup", handleDragEnd as any);
    window.addEventListener("touchend", handleDragEnd as any);
  };

  const handleDragMove = (e: MouseEvent | TouchEvent) => {
    e.preventDefault();
    if (startY.current === null) return;
    let currentY: number | undefined;
    if ("touches" in e) {
      currentY = (e as TouchEvent).touches[0]?.clientY;
    } else {
      currentY = (e as MouseEvent).clientY;
    }
    if (typeof currentY !== "number") return;
    const diff = currentY - startY.current;
    if (diff > 0) {
      setTranslateY(`${diff}px`);
    }
  };

  const handleDragEnd = (e: MouseEvent | TouchEvent) => {
    if (startY.current === null) return;
    let endY: number | undefined;
    if ("changedTouches" in e) {
      endY = (e as TouchEvent).changedTouches[0]?.clientY;
    } else {
      endY = (e as MouseEvent).clientY;
    }
    if (typeof endY !== "number") endY = 0;
    const diff = endY - startY.current;
    setTranslateY("0");
    setIsDragging(false);
    startY.current = null;
    window.removeEventListener("mousemove", handleDragMove as any);
    window.removeEventListener("touchmove", handleDragMove as any);
    window.removeEventListener("mouseup", handleDragEnd as any);
    window.removeEventListener("touchend", handleDragEnd as any);
    if (diff > DRAG_CLOSE_DISTANCE && onClose) {
      setTranslateY("100%");
      setTimeout(() => {
        onClose();
      }, closeTimeoutMS);
    }
  };


  return (
    <ModalSheet
      open={open}
      centered={centered}
      className={className}
      portalClassName={portalClassName}
      children={children}
      closeTimeoutMS={closeTimeoutMS}
      title={title}
      translateY={translateY}
      isDragging={isDragging}
      onDragStart={handleDragStart}
      onClose={onClose}
      position={position}
      onAfterOpen={modalOpenCallback}
      onAfterClose={modalCloseCallback}
    />
  );
};

export default ModalSheetContainer;
